import React from 'react';
import jquery from 'jquery';
import VisibleArticles from './visible-articles';
import Pagination from './pagination';

/**
 * Class to handle the related category articles.
 */
export default class RelatedArticles extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            /**@type {String} */
            datasourceId: props.datasourceId,
            /**@type {Array<Array<Object>>} */
            relatedArticles: [],
            articleCount: 0,
            articleCountMessage: '',
            articlesPerPage: 12,
            /**@type {Array<Object>} */
            data: [],
            currentPage: 1,
            /**@type {Array<Object>} */
            visibleArticles: [],
            pageCount: 0,
            noArticlesMessage: '',
            isMobile: false,
            pageTitle: document.title
        };

        this.handlePaginationClick = this.handlePaginationClick.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.article_count = React.createRef();

    }


    checkMobile() {
        const isMobile = window.matchMedia('(max-width: 500px)').matches;
        if(isMobile === this.state.isMobile) {
            return;
        }

        if (isMobile) {
            this.setState({
                isMobile: true
            });
        }
        else {
            this.setState({
                isMobile: false
            });
        }
    }

    /**
     * Once the component has mounted, calls for the related article JSON data.
     */
    async componentDidMount() {
        this.checkMobile();
        let articleData;

        await jquery.ajax({
            url: '/post/contentHub/GetArticlesByCategory',
            dataType: 'json',
            type: 'POST',
            data: {
                scController: 'ContentHubGetArticles',
                scAction: 'GetArticlesByCategory',
                datasourceId: this.state.datasourceId
            },
            success(data) {
                if (data) {
                    articleData = data;
                }
            },
            error(data) {
                console.warn('Error requesting related articles', data);
            }
        });

        /**@type {Array<Object>} */
        const relatedArticleData = articleData.related_articles;
        if (!relatedArticleData || relatedArticleData.length === 0) {
            this.setState({
                noArticlesMessage: 'No articles were found for this category.'
            });
            return;
        }

        this.setStateFromRelatedArticleData(relatedArticleData);
        window.addEventListener('resize', this.handleResize);
    }

    /**
     * Creates the article count message based on the current page and the current articles
     * to be displayed per page. Example: Article 13 - 24 of 100
     * @param {Array<Object>} visibleArticles The set of articles to be displayed in the current panel.
     * @param {Number} currentPage The current page to be used for the article counts.
     */
    generateArticleCountMessage(visibleArticles, currentPage = this.state.currentPage) {
        let lastArticleCount, firstArticleCount;

        if (visibleArticles.length < this.state.articlesPerPage) {
            lastArticleCount = this.state.articleCount;
            firstArticleCount = this.state.articleCount - (visibleArticles.length - 1);
        }
        else {
            lastArticleCount = currentPage * this.state.articlesPerPage;
            firstArticleCount = lastArticleCount - (this.state.articlesPerPage - 1);
        }

        return `Article ${firstArticleCount} - ${lastArticleCount} of ${this.state.articleCount}`;
    }

    /**
     * Handles a click on the pagination links and updates the state based on the new current page.
     * @param {HTMLAnchorElement} target 
     */
    handlePaginationClick(target) {
        let currentPage = this.state.currentPage;
        let refocus = false
        if(target.classList.contains('pagination__next') && this.state.currentPage !== this.state.pageCount) {
            currentPage = currentPage + 1;
            refocus=true;
        }
        else if (target.classList.contains('pagination__previous') && this.state.currentPage !== 1) {
            currentPage = currentPage - 1;
            refocus=true;
        }
        else if (target.dataset.pageNum && (Number(target.dataset.pageNum) !== currentPage)) {
            currentPage = parseInt(target.dataset.pageNum);
            refocus=true;
        }
        else {
            //Ignore all other clicks.
            return;
        }

        this.setState({
            currentPage: currentPage,
            visibleArticles: this.state.relatedArticles[currentPage - 1],
            articleCountMessage: this.generateArticleCountMessage(this.state.relatedArticles[currentPage - 1], currentPage)
        });

        this.updatePageTitle(currentPage); //Pass through as setState is not always 100% synchronus.

        if (refocus) {             
            this.article_count.current.focus();
            this.article_count.current.scrollIntoView({block: 'center'});
        }
    }


    handleResize() {
        this.checkMobile();
    }

    /**
     * Sets the initial state after the related articles data is returned.
     * @param {Array<Object>} relatedArticleData 
     */
    setStateFromRelatedArticleData(relatedArticleData) {

        this.state.articleCount = relatedArticleData.length;
        const relatedArticles = this.splitArticles([...relatedArticleData]);
        const articleCountMessage = this.generateArticleCountMessage(relatedArticles[this.state.currentPage - 1]);
        this.setState({
            data: relatedArticleData,
            relatedArticles: relatedArticles,
            articleCountMessage: articleCountMessage,
            visibleArticles: relatedArticles[this.state.currentPage - 1],
            pageCount: relatedArticles.length
        });
    }

    /**
     * Splits the 1D array into a 2D array to be used with the pagination.
     * @param {Array<Object>} relatedArticleData The full JSON array of article objects.
     * @returns {Array<Array<Object>>} the mapped 2D array of related articles.
     */
    splitArticles(relatedArticleData) {
        /**@type {Array <Array <Object>>} */
        const relatedArticles = [];

        if(relatedArticleData.length == 0) {
            return relatedArticles;
        }

        while(relatedArticleData.length) {
            relatedArticles.push(relatedArticleData.splice(0, this.state.articlesPerPage))
        }

        return relatedArticles;
    }

    /**
     * Updates the page title based on the current page in the user's pagination.
     * @param {Number} currentPage The page number the user has navigated to.
     */
    updatePageTitle(currentPage) {
        if (currentPage === 1) {
            document.title = this.state.pageTitle;
        }
        else {
            document.title = `Page ${currentPage} - ${this.state.pageTitle}`
        }
    }

    /**
     * Renders the related articles component.
     */
    render() {
        return (
            <div className='c-h-category-related-articles'>
                <div className='c-h-category-related-articles__article-count' ref={this.article_count} tabindex='-1' role="status" aria-label={`Related Articles showing ${this.state.articleCountMessage.replace('-','through')}`}>
                    <span>{this.state.articleCountMessage}</span>
                </div>
                <div className='c-h-category-related-articles__no-articles'>
                    {this.state.noArticlesMessage}
                </div>
                {<VisibleArticles visibleArticles={this.state.visibleArticles}/>}
                {<Pagination 
                    currentPage={this.state.currentPage}
                    pageCount={this.state.pageCount}
                    handlePageClick={this.handlePaginationClick}
                    isMobile={this.state.isMobile}/>}
            </div>
        )
    }
}